// The app by phase: sign-in, first workspace, the window. The server selects Clerk or the self-hosted email flow.
// Without an account, Tally runs as a guest in this browser (api/guest.ts).
import { useEffect, useState } from 'react';
import { ApiError } from '../api/client.ts';
import { switchGuest } from '../api/guest.ts';
import { navigate, useRoute } from '../lib/router.ts';
import { Help, HelpLinks } from '../screens/Help.tsx';
import { newId } from '../model/mutations.ts';
import { Banner, Field, Loading, TextInput, onValue } from '../components/common.tsx';
import { useStore } from '../store/hooks.tsx';
import type { Store } from '../store/store.ts';
import { Button } from '../ui/ds.tsx';
import { Shell, Wordmark } from './Shell.tsx';
import { useAccountAuth } from './auth.tsx';
import { Workspaces } from '../components/Workspaces.tsx';

export function App() {
  const store = useStore();
  const route = useRoute();
  const busy = store.phase === 'loading';
  useEffect(() => {
    document.documentElement.lang = store.lang;
  }, [store.lang]);
  useEffect(() => {
    if (store.phase === 'ready' && new URLSearchParams(window.location.search).has('invite')) navigate({ name: 'settings', tab: 'account' });
  }, [store.phase]);
  if (route.name === 'privacy' || route.name === 'support') return <div className="app"><Help page={route.name} /></div>;
  return (
    <div className="app" data-phase={store.phase} aria-busy={busy}>
      {store.phase === 'loading' ? <div className="center"><Loading label={store.t('Lädt …')} /></div>
        : store.phase === 'signedOut' ? <SignIn />
        : store.phase === 'needsWorkspace' ? <Welcome />
        : store.phase === 'failed' ? <Failed />
        : <Shell />}
    </div>
  );
}

function describe(store: Store, error: unknown): string {
  if (error instanceof ApiError && (error.code === 'invalid_code')) return store.t('Der Code stimmt nicht oder ist abgelaufen.');
  if (error instanceof ApiError && error.code === 'invalid_email') return store.t('Bitte gib eine gültige E-Mail-Adresse ein.');
  return store.describe(error);
}

function SignIn() {
  const store = useStore();
  const auth = useAccountAuth();
  const t = store.t;
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The link in the sign-in mail: /login?token=…
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (auth.provider !== 'email' || window.location.pathname !== '/login' || !token) return;
    setWorking(true);
    store.api.verifyLink(token)
      .then(() => { window.history.replaceState(null, '', '/'); return store.reload(); })
      .catch((e: unknown) => { setError(describe(store, e)); setWorking(false); });
  }, [store, auth.provider]);

  const send = async () => {
    setWorking(true);
    setError(null);
    try {
      await store.api.startEmail(email.trim(), store.lang);
      setSent(true);
    } catch (e) {
      setError(describe(store, e));
    } finally {
      setWorking(false);
    }
  };
  const verify = async () => {
    setWorking(true);
    setError(null);
    try {
      await store.api.verifyEmail(email.trim(), code.trim());
      navigate('/', { replace: true });
      await store.reload();
    } catch (e) {
      setError(describe(store, e));
      setWorking(false);
    }
  };
  return (
    <div className="auth">
      <div className="auth__card tl-card">
        <Wordmark />
        <h1>{t('Anmelden oder Konto erstellen')}</h1>
        <p className="auth__lead">{auth.provider === 'email'
          ? t('Deine Zeit und die deiner Agents, auf jedem Gerät. Wir schicken dir einen Code per E-Mail, ein Passwort gibt es nicht. Ist die Adresse neu, legt der erste Code dein Konto an.')
          : t('Deine Zeit und die deiner Agents, auf jedem Gerät. Melde dich mit deinem Tellary-Konto an oder erstelle ein neues Konto.')}</p>
        {store.guestIds() ? <Banner tone="neutral" icon="user">{t('Deine Daten aus dem Gastmodus kommen nach der Anmeldung in dein Konto.')}</Banner> : null}
        {error || auth.error ? <Banner tone="danger">{error ?? auth.error}</Banner> : store.problem ? <Banner tone="waiting" onDismiss={() => store.dismissProblem()} dismissLabel={t('Schließen')}>{store.problem}</Banner> : null}
        {auth.provider === 'clerk' ? auth.signIn : auth.provider === 'loading' ? <Loading label={t('Anmeldung wird geladen …')} />
          : auth.provider === 'unavailable' ? <>
            <Banner tone="danger">{t('Die Anmeldung ist gerade nicht erreichbar. Versuche es erneut oder fahre als Gast fort.')}</Banner>
            <Button onClick={auth.retry}>{t('Erneut versuchen')}</Button>
          </> : !sent ? (
          <form className="auth__form" onSubmit={(e) => { e.preventDefault(); void send(); }}>
            <Field label={t('E-Mail')}>
              <TextInput type="email" autoComplete="email" required value={email} onChange={onValue(setEmail)} placeholder={t('du@firma.de')} autoFocus />
            </Field>
            <Button type="submit" variant="primary" size="lg" disabled={working || !email.includes('@')}>{t('Code senden')}</Button>
          </form>
        ) : (
          <form className="auth__form" onSubmit={(e) => { e.preventDefault(); void verify(); }}>
            <p className="auth__hint">{t('Wir haben einen 6-stelligen Code an %@ geschickt. Er gilt 15 Minuten. Du kannst auch auf den Link in der E-Mail klicken.', email.trim())}</p>
            <Field label={t('Code')}>
              <TextInput inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code}
                onChange={onValue((v) => setCode(v.replace(/\D/g, '')))} placeholder="123456" autoFocus className="input--code" />
            </Field>
            <Button type="submit" variant="primary" size="lg" disabled={working || code.length !== 6}>{t('Anmelden')}</Button>
            <Button variant="plain" size="sm" onClick={() => { setSent(false); setCode(''); }}>{t('Andere Adresse')}</Button>
          </form>
        )}
        {!sent ? (
          <div className="auth__guest">
            <span className="auth__or">{t('oder')}</span>
            <Button variant="tinted" size="lg" onClick={() => switchGuest(true)}>{t('Als Gast fortfahren')}</Button>
            <p className="auth__hint">{t('Als Gast bleiben deine Daten in diesem Browser. Tellary Pro und der Abgleich mit deinen Geräten brauchen ein Konto.')}</p>
          </div>
        ) : null}
        <p className="auth__foot">{t('Prompts, Code und Tool-Ausgaben deiner Agents kommen nie hier an. Tellary zählt nur, wann sie arbeiten.')}</p>
        <HelpLinks />
      </div>
    </div>
  );
}

function Welcome() {
  const store = useStore();
  const auth = useAccountAuth();
  const t = store.t;
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = store.me?.user.name || (store.me?.user.email ?? '').split('@')[0] || '';
  const create = async () => {
    setWorking(true);
    try {
      // A former guest's workspace keeps its ids, so its data moves over unchanged.
      const guest = store.guestIds();
      await store.api.claimWorkspace({ workspaceId: guest?.workspaceId ?? newId(), memberId: guest?.memberId ?? newId(), name: 'Tellary', memberName: name });
      await store.reload();
    } catch (e) {
      setError(store.describe(e));
      setWorking(false);
    }
  };
  return (
    <div className="auth">
      <div className="auth__card tl-card">
        <Wordmark />
        <h1>{t('Willkommen bei Tellary')}</h1>
        <p className="auth__lead">{t('Für %@ gibt es noch keinen Arbeitsbereich. Leg ihn hier an; deine Macs kommen dazu, sobald du dich dort mit derselben Adresse anmeldest, und bringen ihre Daten mit.', store.me?.user.email ?? '')}</p>
        {error || auth.error ? <Banner tone="danger">{error ?? auth.error}</Banner> : null}
        <Button variant="primary" size="lg" onClick={() => void create()} disabled={working}>{t('Arbeitsbereich anlegen')}</Button>
        <Button variant="plain" size="sm" disabled={auth.pending} onClick={() => void auth.signOut()}>{t('Abmelden')}</Button>
        {store.workspaces?.pendingInvites.length ? <Workspaces /> : null}
        <HelpLinks />
      </div>
    </div>
  );
}

function Failed() {
  const store = useStore();
  const t = store.t;
  return (
    <div className="auth">
      <div className="auth__card tl-card">
        <Wordmark />
        <h1>{t('Tellary ist gerade nicht erreichbar')}</h1>
        <Banner tone="danger">{store.problem ?? t('Etwas ist schiefgelaufen')}</Banner>
        <Button variant="primary" onClick={() => void store.start()}>{t('Erneut versuchen')}</Button>
        <Button variant="tinted" onClick={() => switchGuest(true)}>{t('Als Gast fortfahren')}</Button>
        <HelpLinks />
      </div>
    </div>
  );
}
